import { UnitConverterPanel, UnitConverterPanelResult } from "@foodpilot/foods";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Property } from "src/api/sure-api/ssq/useGetOneSsqVersion";
import { UnitConverterOption } from "src/utils/buildUnitConverterOptions";
import {
    formatConversionFactor,
    formatNumberByType,
    formatUnitAbbreviation,
    parseLocalizedNumber,
} from "src/utils/formatting";
import { convertToQuestionUnit } from "src/utils/unitConversion";
import { buildUnitConversion, findConversionSourceOption } from "src/utils/unitConversionRecord";
import { UnitConversion } from "src/api/client-api/post/postSchema";
import { PropertyUpdateOutcome } from "../context/context";
import { useQuestionnaireFormContext } from "../../context/useQuestionnaireForm";
import { unitConverterElementIds } from "./UnitConverterButton";

type Props = {
    converterKey: string;
    property: Property;
    options: UnitConverterOption[];
    unitConversion: UnitConversion | null;
    onApply: (canonicalValue: string, unitConversion: UnitConversion) => Promise<PropertyUpdateOutcome>;
};

export const UnitConverterSection = (props: Props) => {
    const { converterKey, property, options, unitConversion, onApply } = props;
    const { t, i18n } = useTranslation();
    const { setOpenUnitConverterKey } = useQuestionnaireFormContext();

    const locale = i18n.resolvedLanguage ?? "en";
    const [sourceValue, setSourceValue] = useState(() =>
        unitConversion ?
            new Intl.NumberFormat(locale, { useGrouping: false, maximumFractionDigits: 20 }).format(
                Number(unitConversion.sourceValue),
            )
        :   "",
    );
    const [selectedOptionId, setSelectedOptionId] = useState<string | null>(
        () => (unitConversion && findConversionSourceOption(options, unitConversion)?.id) ?? null,
    );
    const [saving, setSaving] = useState(false);
    const [rejection, setRejection] = useState<string | null>(null);

    const targetUnit = formatUnitAbbreviation(property.unit);
    const ids = unitConverterElementIds(converterKey);
    const selectedOption = options.find((option) => option.id === selectedOptionId) ?? null;

    const conversion = convertToQuestionUnit({
        sourceValue,
        locale,
        factor: selectedOption?.factor ?? null,
        precision: property.precision,
        minValue: property.minValue,
        maxValue: property.maxValue,
    });

    const withUnit = (value: string | number) =>
        `${formatNumberByType(value, property.unit?.title, locale, property.precision ?? 20)} ${targetUnit}`;

    const factorDetail = () => {
        if (!selectedOption) {
            return "";
        }

        const factor = t("ssq.unit_converter.factor_detail", "1 {{source}} = {{factor}} {{target}}", {
            source: selectedOption.label,
            factor: formatConversionFactor(selectedOption.factor, locale),
            target: targetUnit,
        });
        if (!selectedOption.source) {
            return factor;
        }

        return `${factor} · ${t("ssq.unit_converter.source_detail", "Source: {{source}}", { source: selectedOption.source })}`;
    };

    const buildResult = (): UnitConverterPanelResult | null => {
        if (conversion.status === "incomplete") {
            return null;
        }

        const value = withUnit(conversion.canonicalValue);
        if (conversion.status === "out_of_range") {
            const detail =
                conversion.bound === "max" ?
                    t(
                        "ssq.unit_converter.above_max",
                        "Above the maximum accepted for this question ({{limit}} {{unit}}). Check the value or the unit.",
                        {
                            limit: formatNumberByType(conversion.limit, property.unit?.title, locale, 20),
                            unit: targetUnit,
                        },
                    )
                :   t(
                        "ssq.unit_converter.below_min",
                        "Below the minimum accepted for this question ({{limit}} {{unit}}). Check the value or the unit.",
                        {
                            limit: formatNumberByType(conversion.limit, property.unit?.title, locale, 20),
                            unit: targetUnit,
                        },
                    );

            return {
                title: t("ssq.unit_converter.result_title_out_of_range", "Converted value"),
                value,
                detail,
                error: true,
            };
        }
        if (rejection !== null) {
            return {
                title: t("ssq.unit_converter.result_title_not_saved", "Value not saved"),
                value,
                detail: rejection,
                error: true,
            };
        }

        return {
            title: t("ssq.unit_converter.result_title", "Value that will be saved"),
            value,
            detail: factorDetail(),
        };
    };

    const applyLabel = () => {
        if (saving) {
            return t("ssq.unit_converter.saving", "Saving…");
        }
        return t("ssq.unit_converter.apply_converted_value", "Use converted value");
    };

    const close = () => {
        setOpenUnitConverterKey(null);
        document.getElementById(ids.button)?.focus();
    };

    const apply = async () => {
        if (conversion.status !== "ready" || !selectedOption) {
            return;
        }

        setSaving(true);
        setRejection(null);
        const outcome = await onApply(
            conversion.canonicalValue,
            buildUnitConversion(selectedOption, parseLocalizedNumber(sourceValue, locale)),
        );
        if (outcome.saved) {
            close();
            return;
        }

        setSaving(false);
        setRejection(outcome.messages.join(" ") || t("ssq.form.error"));
    };

    return (
        <UnitConverterPanel
            id={ids.panel}
            title={t("ssq.unit_converter.panel_title", "Convert to {{unit}}", { unit: targetUnit })}
            options={options.map((option) => ({
                id: option.id,
                label: option.label,
                groupName:
                    option.kind === "recipe" ?
                        t("ssq.unit_converter.group_recipes", "Conversions")
                    :   t("ssq.unit_converter.group_units", "Other multiples"),
            }))}
            selectedOptionId={selectedOptionId}
            onSelectedOptionIdChange={(id) => {
                setSelectedOptionId(String(id));
                setRejection(null);
            }}
            sourceValue={sourceValue}
            onSourceValueChange={(value) => {
                setSourceValue(value);
                setRejection(null);
            }}
            result={buildResult()}
            applyLabel={applyLabel()}
            applyDisabled={conversion.status !== "ready"}
            applying={saving}
            onApply={apply}
            onCancel={close}
            labels={{
                valuePlaceholder: t("ssq.unit_converter.value_placeholder", "e.g. 1 200"),
                unitPlaceholder: t("ssq.unit_converter.unit_placeholder", "Choose a unit"),
                emptyHint: t(
                    "ssq.unit_converter.empty_hint",
                    "Enter the value from your bill and choose its unit to see the result.",
                ),
                cancel: t("ssq.unit_converter.cancel", "Cancel"),
            }}
        />
    );
};
